/**
 * AeriosIQ brand assets — vector data verbatim from the designer file
 * `ARIOS2.1.svg` (delivered 2026-07-08, archived in the orchestration repo
 * under .vibe/_TRANSIST_FROM_WP_TO_AERIOSIQ/). No hand-tracing.
 *
 * The lockup is deliberately split into independently swappable parts
 * (transition doc 05 SS2): Mark (gradient network shape), Wordmark
 * ("Aerios" letterforms with dotless-i rect, currentColor), Badge
 * (gradient "IQ", Q as magnifier). Swap any part by editing its file only
 * — the composite AeriosIQLogo re-uses the exported path constants at the
 * designer's original coordinates, so spacing never drifts.
 *
 * Gradient (authoritative): #00829F at 0% -> #00303F at 77%.
 * Dark-surface treatment: deferred to the CI pass — the gradient is tuned
 * for light chrome; wordmark letters follow currentColor.
 */
import type { SVGProps } from "react";
import { useTheme } from "@mui/material/styles";

/**
 * Theme-aware fill (Christian, 2026-07-08): dark theme renders the ENTIRE
 * logo monochrome white (gradient's petrol end sinks into dark chrome);
 * light theme keeps the colored gradient. `variant` overrides the
 * auto-detection when a surface needs a fixed treatment.
 */
type LogoVariant = "auto" | "color" | "mono";

import { AERIOSIQ_BADGE_PATH } from "./AeriosIQBadge.js";

/** "Aerios" letterform paths (the dotless i is the <rect>). currentColor. */
export const AERIOSIQ_WORDMARK_PATHS = [
  "M95.53,37.88h-24.36l-4.8,11.28h-9.33L78.96.43h8.91l22,48.73h-9.47l-4.87-11.28ZM92.54,30.78l-9.19-21.3-9.12,21.3h18.31Z",
  "M148.02,33.29h-29.17c1.04,5.43,5.57,8.91,12.04,8.91,4.18,0,7.45-1.32,10.09-4.04l4.66,5.36c-3.34,3.97-8.56,6.06-14.97,6.06-12.46,0-20.54-8.01-20.54-19.07s8.15-19,19.21-19,18.8,7.59,18.8,19.21c0,.7-.07,1.74-.14,2.58ZM118.78,27.51h21.09c-.7-5.36-4.8-9.05-10.51-9.05s-9.75,3.62-10.58,9.05Z",
  "M176.39,11.62v8.27c-.76-.14-1.39-.21-2.02-.21-6.6,0-10.77,3.89-10.77,11.46v18h-8.69V12.04h8.27v5.42c2.5-3.89,7.02-5.84,13.2-5.84Z",
  "M198.59,30.55c0-11.16,8.37-19.04,19.81-19.04s19.88,7.88,19.88,19.04-8.3,19.11-19.88,19.11-19.81-7.95-19.81-19.11ZM229.49,30.55c0-7.11-4.74-11.65-11.09-11.65s-11.02,4.53-11.02,11.65,4.74,11.65,11.02,11.65,11.09-4.53,11.09-11.65Z",
  "M241.29,45.67l2.92-6.47c3.13,2.28,7.79,3.87,12.24,3.87,5.09,0,7.15-1.7,7.15-3.97,0-6.94-21.57-2.17-21.57-15.95,0-6.31,5.09-11.55,15.63-11.55,4.66,0,9.43,1.11,12.88,3.29l-2.65,6.52c-3.44-1.96-7-2.92-10.28-2.92-5.09,0-7.05,1.91-7.05,4.24,0,6.84,21.57,2.12,21.57,15.74,0,6.2-5.14,11.5-15.74,11.5-5.88,0-11.82-1.75-15.1-4.29Z",
] as const;
export const AERIOSIQ_WORDMARK_RECT = {
  x: 183.68,
  y: 12.06,
  width: 8.67,
  height: 37.14,
} as const;

/** Wordmark = letters + IQ badge, no mark. Letters inherit currentColor;
 * the badge keeps its brand gradient. */
export function AeriosIQWordmark({
  variant = "auto",
  ...props
}: SVGProps<SVGSVGElement> & { variant?: LogoVariant }) {
  const mode = useTheme().palette.mode;
  const mono = variant === "mono" || (variant === "auto" && mode === "dark");
  return (
    <svg
      // eslint-disable-next-line no-restricted-syntax -- SVG namespace URI is canonical http://; not an OWASP V9.1.1 concern.
      xmlns="http://www.w3.org/2000/svg"
      viewBox="57.04 0 255.32 49.96"
      role="img"
      aria-label="AeriosIQ"
      height={24}
      fill="currentColor"
      {...props}
    >
      <defs>
        <linearGradient
          id="aiq-grad-badge-w"
          x1="280.91"
          y1="12.82"
          x2="312.36"
          y2="12.82"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#00829f" />
          <stop offset=".77" stopColor="#00303f" />
        </linearGradient>
      </defs>
      {AERIOSIQ_WORDMARK_PATHS.map((d) => (
        <path key={d.slice(0, 24)} d={d} />
      ))}
      <rect
        x={AERIOSIQ_WORDMARK_RECT.x}
        y={AERIOSIQ_WORDMARK_RECT.y}
        width={AERIOSIQ_WORDMARK_RECT.width}
        height={AERIOSIQ_WORDMARK_RECT.height}
      />
      <path
        fill={mono ? "currentColor" : "url(#aiq-grad-badge-w)"}
        d={AERIOSIQ_BADGE_PATH}
      />
    </svg>
  );
}
